import { cn } from "../lib/cn";
import tileCap from "../assets/tile-cap.webp";
import tileHub from "../assets/tile-carrierhub.webp";
import tileCapGo from "../assets/tile-cap-go.webp";
import tileCapFlow from "../assets/tile-cap-flow.webp";
import tileCapScale from "../assets/tile-cap-scale.webp";
import shadowCap from "../assets/shadow-cap.webp";
import shadowHub from "../assets/shadow-carrierhub.webp";
import glowWarm from "../assets/glow-warm.svg";
import glowGreen from "../assets/glow-green.svg";

export type Brand = "cap" | "carrierhub";
export type CapModuleName = "GO" | "FLOW" | "SCALE";

const TILE: Record<Brand, string> = { cap: tileCap, carrierhub: tileHub };
const SHADOW: Record<Brand, string> = { cap: shadowCap, carrierhub: shadowHub };
const GLOW: Record<Brand, string> = { cap: glowWarm, carrierhub: glowGreen };
const ALT: Record<Brand, string> = { cap: "CAP", carrierhub: "Carrier Hub" };

/**
 * Fertige Modulkacheln vom Kunden – Kachel, Sockel mit Modulnamen und
 * Schatten stecken bereits in der Grafik.
 */
const MODULE_TILE: Record<CapModuleName, string> = {
  GO: tileCapGo,
  FLOW: tileCapFlow,
  SCALE: tileCapScale,
};

/*
 * Am Entwurf abgemessen (Verhältnisse zur Kachelgrafik, deren sichtbarer
 * Körper von 10,5 % bis 89,2 % der Grafikhöhe reicht):
 *
 * - Der warme Lichtschein liegt als Pfütze UNTER der Kachel, seine Mitte auf
 *   96 % – nicht hinter ihr. Lag er zu hoch, entstand der gelbe Streifen
 *   quer hinter dem Icon.
 * - Die Schattenellipse sitzt knapp darunter auf 97 %.
 *
 * Beide Grafiken tragen ihren Blendmodus intern (overlay bzw. multiply). Ueber
 * ein <img> geladen wirkt der aber nur innerhalb der Grafik, nicht gegen den
 * Seitenhintergrund – der gelbe Schein lag dadurch roh obendrauf statt sich
 * zum gruenen Lichtschein des Entwurfs zu verrechnen. Deshalb stehen die
 * Blendmodi hier am Element, und der Container isoliert bewusst nicht.
 */
const GLOW_CENTER = "96%";

/*
 * Die Schattenellipse ist im Entwurf nachgemessen (Halbwertsbreite, also die
 * Stelle, an der die Verdunklung auf die Haelfte abgefallen ist):
 *
 * - Sie ist 229 von 442 Kacheleinheiten breit, also 51,9 % der Kachelbreite.
 * - Ihre Mitte liegt auf 100 % der Kachelhoehe, gemessen ab Grafikoberkante.
 *
 * In der Grafik selbst sitzt dieser Kern nicht mittig: er ist 54,4 % der
 * Grafikbreite breit, seine Mitte liegt bei 39,1 % der Breite und 61,7 % der
 * Hoehe. Daraus folgen Breite (51,9 / 54,4 ≈ 95 %) und Ankerpunkt unten.
 */
const SHADOW_CENTER = "99.5%";
const SHADOW_WIDTH = "95%";
const SHADOW_ANCHOR = "translate(-39.1%, -61.7%)";

export type ProductTileProps = {
  brand: Brand;
  /** Modulname – rendert die fertige Modulkachel statt der einfachen. */
  label?: CapModuleName;
  /** Breite der Kachel – beliebige CSS-Länge. */
  width?: string;
  /** Weiche Schatten-Ellipse unter der Kachel (bei Modulkacheln eingebaut). */
  shadow?: boolean;
  /** Breiter Lichtschein unter der Kachel (nur im Hero). */
  glow?: boolean;
  /** Sanftes Schweben. */
  float?: boolean;
  className?: string;
  /** `true`, wenn direkt daneben bereits der Produktname steht. */
  decorative?: boolean;
};

export function ProductTile({
  brand,
  label,
  width = "16rem",
  shadow = true,
  glow = false,
  float = false,
  decorative = false,
  className,
}: ProductTileProps) {
  // Bei den Modulkacheln sind Sockel und Schatten Teil der Grafik.
  const isModule = label !== undefined;
  const src = isModule ? MODULE_TILE[label] : TILE[brand];
  const alt = isModule ? `${ALT[brand]} ${label}` : ALT[brand];

  return (
    <div
      className={cn("relative mx-auto", className)}
      style={{ width }}
    >
      {glow && !isModule && (
        <img
          src={GLOW[brand]}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 z-0 w-[448%] max-w-none -translate-x-1/2 -translate-y-1/2 mix-blend-overlay select-none"
          style={{ top: GLOW_CENTER }}
        />
      )}
      {shadow && !isModule && (
        <img
          src={SHADOW[brand]}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 z-0 max-w-none mix-blend-multiply select-none"
          style={{ top: SHADOW_CENTER, width: SHADOW_WIDTH, transform: SHADOW_ANCHOR }}
        />
      )}
      <div className={cn("relative z-10", float && "vcn-float")}>
        <img
          src={src}
          alt={decorative ? "" : alt}
          aria-hidden={decorative || undefined}
          className="vcn-tile-glow relative block w-full select-none"
          draggable={false}
        />
      </div>
    </div>
  );
}
